import React, { forwardRef } from 'react';

import { type MessageDescriptor, useIntl } from 'react-intl-next';

import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import type { TriggerProps } from '@atlaskit/popup';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Pressable, type PressableProps, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { ColorTile, type ColorTileProps } from '../../common/ui/color-tile';

import { messages } from './messages';
import { TextColorIcon } from './text-color-icon';

type ColorPickerTriggerProps = {
	analyticsSource?: string;
	pickerTitle?: MessageDescriptor;
} & TriggerProps &
	Pick<PressableProps, 'onClick'> &
	Pick<PressableProps, 'isDisabled'> &
	Pick<ColorTileProps, 'builtInColorMap' | 'value'> & {
		enableBorderLessStyle?: boolean;
		isFullWidth?: boolean;
		showTextIcon?: boolean;
	};

// taken from design-system icon types as it isn't exported from there
type UtilityIconSpacing = 'none' | 'spacious' | 'compact';

const triggerStyles = xcss({
	background: token('color.background.input'),
	border: `${token('border.width')} solid ${token('color.border')}`,
	transition: 'background 0.1s ease-out',
	borderRadius: 'radius.small',
	padding: 'space.100',
	backgroundColor: 'elevation.surface.overlay',
	':hover': {
		backgroundColor: 'elevation.surface.overlay.hovered',
	},
	':active': {
		backgroundColor: 'elevation.surface.overlay.pressed',
	},
});

const selectedButtonStyles = xcss({
	backgroundColor: 'color.background.selected',
	borderColor: 'color.border.selected',
	':hover': {
		backgroundColor: 'color.background.selected.hovered',
	},
	':active': {
		backgroundColor: 'color.background.selected.pressed',
	},
});

const borderLessStyle = xcss({
	border: 'none',
});

const fullWidthStyle = xcss({
	width: '240px',
});

const iconOnlyStyle = xcss({
	paddingBlock: 'space.050',
});

const triggerContentStyles = xcss({
	alignItems: 'center',
});

const iconOnlyContentStyles = xcss({
	gap: 'space.100',
});

const fullWidthContentStyles = xcss({
	gap: 'space.075',
});

const colorLabelStyles = xcss({
	color: 'color.text.subtlest',
	display: 'inline-block',
	flex: '1 0 0',
	overflow: 'hidden',
	textAlign: 'left',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

export const ColorPickerTrigger: React.ForwardRefExoticComponent<Omit<ColorPickerTriggerProps, "ref"> & React.RefAttributes<HTMLButtonElement>> = forwardRef<HTMLButtonElement, ColorPickerTriggerProps>(
	(
		{
			builtInColorMap,
			value,
			analyticsSource,
			pickerTitle,
			isFullWidth = false,
			showTextIcon = false,
			enableBorderLessStyle = false,
			isDisabled = false,
			...props
		},
		ref,
	) => {
		const { formatMessage } = useIntl();

		const colorName = builtInColorMap.humanReadableColors.get(value);
		const size = !isFullWidth ? '16' : '24';
		const spacing = !isFullWidth ? 'none' : 'spacious';
		const padding = size === '16' ? 'space.050' : 'space.0';
		const isOpen = props['aria-expanded'];
		const label = `${formatMessage(pickerTitle || messages.triggerPrefixLabel)} ${formatMessage(messages.triggerPostfixLabel)}`;

		return (
			<Pressable
				{...props}
				aria-label={label}
				title={label}
				isDisabled={isDisabled}
				aria-disabled={isDisabled}
				ref={ref}
				xcss={[
					triggerStyles,
					isFullWidth ? fullWidthStyle : iconOnlyStyle,
					isOpen && selectedButtonStyles,
					enableBorderLessStyle && borderLessStyle,
				]}
			>
				<Inline
					xcss={[
						triggerContentStyles,
						isFullWidth ? fullWidthContentStyles : iconOnlyContentStyles,
					]}
				>
					{showTextIcon ? (
						<TextColorIcon
							testId="color-picker-trigger-text-icon"
							selectedColor={value}
							isSelected={isOpen}
							isDisabled={isDisabled}
						/>
					) : (
						<Box padding={padding}>
							<ColorTile
								testId="color-picker-trigger-color-tile"
								size={size}
								builtInColorMap={builtInColorMap}
								value={value}
							/>
						</Box>
					)}
					{isFullWidth && (
						<Inline xcss={colorLabelStyles}>
							{colorName ? formatMessage(colorName) : value.toUpperCase()}
						</Inline>
					)}
					<ExpandCollapseIcon spacing={spacing} isExpanded={isOpen} isDisabled={isDisabled} />
				</Inline>
			</Pressable>
		);
	},
);

const iconContainerStyles = xcss({
	flexShrink: 0,
});

const chevronPaddingStyles = xcss({
	paddingRight: 'space.050',
});

const ExpandCollapseIcon = ({
	isExpanded = false,
	spacing,
	isDisabled,
}: {
	isDisabled?: boolean;
	isExpanded: boolean;
	spacing: UtilityIconSpacing;
}) => {
	let textColor: ReturnType<typeof token> = isExpanded
		? token('color.text.selected')
		: token('color.text.subtle');
	if (isDisabled) {
		textColor = token('color.text.disabled');
	}
	return (
		<Box xcss={[iconContainerStyles, spacing === 'none' && chevronPaddingStyles]}>
			<ChevronDownIcon spacing={spacing} label="" color={textColor} size="small" />
		</Box>
	);
};
